// 高中批改結果的格式收斂(kanban 1000517)。
//
// 批改輸出有兩種格式,來源是 prompt:
//   舊:{ 題旨發揮:{等第,得分,評語}, …, 整體評分:{等第,總分,滿分,評語,建議} }
//   新:{ totalScore, categories:[{name,score,maxScore,comment}],
//        overallComment:{comment,suggestions} }
//
// 2026-08-26 起各書 prompt 逐本改用新格式。**既有紀錄永遠是舊格式**(本卡不做
// 資料遷移),所以「兩種都認」不是過渡期措施,是長期狀態 —— 讀取端一律走這裡,
// 不要再各自 parsed["整體評分"]。

export interface NormalizedCategory {
  name: string;
  /** 取不到回 undefined,不補 0 —— 0 是合法分數(離題卷),不能拿來表示「無資料」。
   *  要不要補值由呼叫端決定:成績表 Excel 留空白、成績單 docx 需要 number 才補。 */
  score: number | undefined;
  maxScore?: number;
  comment: string;
  grade?: string;
}

export interface NormalizedCorrection {
  totalScore: number | undefined;
  totalGrade: string | undefined;
  categories: NormalizedCategory[];
  overallComment: string;
  suggestions: string[];
}

interface NewFormatCategory {
  name?: unknown;
  score?: unknown;
  maxScore?: unknown;
  comment?: unknown;
  grade?: unknown;
}

interface NewFormat {
  totalScore?: unknown;
  totalGrade?: unknown;
  categories?: unknown;
  overallComment?: { comment?: unknown; suggestions?: unknown };
}

/** 數字欄位:非數字(含 undefined/null/空字串)一律回 undefined,不補 0 —— 0 是合法分數 */
const toNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

/**
 * 建議欄位在兩種格式都可能是陣列或字串;空值回空陣列。
 *
 * 字串按行拆成多點:新 prompt 對 suggestions 的說明是「建議(字串;多點時每點
 * 一行)」,整串當成一點會在畫面上擠成一大坨。單行字串自然還是一點,對舊格式
 * 那種單句建議無影響。
 * (依據是 prompt 的欄位說明 —— 改格式的書批改全失敗、還沒有成功樣本可佐證,
 *  拿到新格式的真實輸出後要回頭核對這裡的假設。)
 */
const toSuggestions = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
  }
  return [];
};

/** 新格式的判準:頂層有 totalScore 或 categories 陣列 —— 舊格式兩者都沒有 */
const isNewFormat = (obj: Record<string, unknown>): boolean =>
  "totalScore" in obj || Array.isArray((obj as NewFormat).categories);

const fromNewFormat = (obj: NewFormat): NormalizedCorrection => {
  const rawCategories = Array.isArray(obj.categories) ? obj.categories : [];
  return {
    totalScore: toNumber(obj.totalScore),
    totalGrade: typeof obj.totalGrade === "string" ? obj.totalGrade : undefined,
    categories: rawCategories.map((raw) => {
      const c = (raw ?? {}) as NewFormatCategory;
      return {
        name: toText(c.name),
        score: toNumber(c.score),
        maxScore: toNumber(c.maxScore),
        comment: toText(c.comment),
        grade: typeof c.grade === "string" ? c.grade : undefined,
      };
    }),
    overallComment: toText(obj.overallComment?.comment),
    suggestions: toSuggestions(obj.overallComment?.suggestions),
  };
};

interface OldCategoryEntry {
  等第?: unknown;
  得分?: unknown;
  評語?: unknown;
}

interface OldOverallEntry {
  等第?: unknown;
  總分?: unknown;
  滿分?: unknown;
  評語?: unknown;
  建議?: unknown;
}

// 舊格式的兩套構面名稱與判準,鏡射 classReportDownload.getReportCategories
// (有「題旨發揮」→ 高中 25 分制那組,否則是另一組)。兩邊必須一致,否則同一筆
// 紀錄在檢視畫面與下載的成績表會列出不同的構面。
const OLD_CATEGORIES_HS = ["題旨發揮", "資料掌握", "結構安排", "字句應用"];
const OLD_CATEGORIES_ALT = [
  "立意取材",
  "結構組織",
  "遣詞造句",
  "錯別字、格式與標點符號",
];

const oldCategoryNames = (obj: Record<string, unknown>): string[] =>
  obj["題旨發揮"] ? OLD_CATEGORIES_HS : OLD_CATEGORIES_ALT;

const fromOldFormat = (obj: Record<string, unknown>): NormalizedCorrection => {
  const overall = (obj["整體評分"] ?? {}) as OldOverallEntry;
  return {
    totalScore: toNumber(overall.總分),
    totalGrade: typeof overall.等第 === "string" ? overall.等第 : undefined,
    categories: oldCategoryNames(obj).map((name) => {
      const entry = (obj[name] ?? {}) as OldCategoryEntry;
      return {
        name,
        score: toNumber(entry.得分),
        // 舊格式沒有逐項滿分,留 undefined 由呼叫端決定要不要補
        maxScore: undefined,
        comment: toText(entry.評語),
        grade: typeof entry.等第 === "string" ? entry.等第 : undefined,
      };
    }),
    overallComment: toText(overall.評語),
    suggestions: toSuggestions(overall.建議),
  };
};

export const normalizeCorrection = (
  parsed: unknown
): NormalizedCorrection | null => {
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  if (isNewFormat(obj)) return fromNewFormat(obj as NewFormat);
  return fromOldFormat(obj);
};

/**
 * 依指定的向度順序取出分數與評語(兩種格式都適用)。
 *
 * 順序由呼叫端給:成績表 Excel 與成績單 docx 的向度順序刻意不同,兩個都是 PM
 * 分別確認過的(見 classReportDownload),所以這裡不決定順序、只負責取值。
 * 要的向度資料裡沒有時補 0 與空字串 —— 缺一項不該讓整份報告產不出來。
 */
export const orderedCategories = (
  parsed: unknown,
  names: readonly string[]
): { name: string; score: number; comment: string }[] => {
  const normalized = normalizeCorrection(parsed);
  return names.map((name) => {
    const found = normalized?.categories.find((c) => c.name === name);
    return {
      name,
      score: found?.score ?? 0,
      comment: found?.comment ?? "",
    };
  });
};

/**
 * 是不是高中 25 分制那組向度(題旨發揮/資料掌握/結構安排/字句應用)。
 *
 * 原本各頁自己寫 `!!result?.題旨發揮`,新格式把四構面收進 categories 陣列、
 * 頂層沒有那個鍵,會被判成舊制那組 —— 表頭選錯組、每格再也對不到值。
 * 兩種格式都由 normalizeCorrection 收斂後再判。
 */
export const isHsScheme25 = (parsed: unknown): boolean =>
  normalizeCorrection(parsed)?.categories.some((c) => c.name === "題旨發揮") ??
  false;

/**
 * 取單一構面的分數,取不到回 undefined。
 *
 * **不要把取不到補成 0**:離題卷的 0 是真的 0 分,呼叫端要能區分「0 分」與
 * 「這格沒資料」(前者顯示 0、後者顯示 '-')。同 extractTotalScore 的原則。
 */
export const categoryScore = (
  parsed: unknown,
  name: string
): number | undefined =>
  normalizeCorrection(parsed)?.categories.find((c) => c.name === name)?.score;

/**
 * 這筆批改結果有沒有「可編輯的結構」(kanban 106732 老師手動調整評語)。
 *
 * 舊行為是 `!!parsed.整體評分`,新格式沒有那個鍵 —— 編輯入口會靜默消失,
 * 老師只是看不到按鈕、沒有任何錯誤訊息,很難發現。兩種格式都要認得。
 * 判準取「整體評語容器」,與後端 teacher_edit_record 的判法一致。
 */
export const hasEditableStructure = (parsed: unknown): boolean => {
  if (parsed === null || typeof parsed !== "object") return false;
  const obj = parsed as Record<string, unknown>;
  const oldOk = typeof obj["整體評分"] === "object" && obj["整體評分"] !== null;
  const newOk =
    typeof obj.overallComment === "object" && obj.overallComment !== null;
  return oldOk || newOk;
};

/** 這筆有沒有某個評分項(編輯基準值只收錄紀錄裡實際存在的項目) */
export const hasCategory = (parsed: unknown, name: string): boolean =>
  normalizeCorrection(parsed)?.categories.some((c) => c.name === name) ?? false;
